import React, { useState, Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { Download, Briefcase, Loader2 } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
import jsPDF from 'jspdf';
const PHOTO_URL = 'https://cdn.magicpatterns.com/uploads/6LWFciUpqXzvc9S9PJFRtx/Scherm%C2%ADafbeelding_2026-04-19_om_14.10.57.png';
function loadImageAsBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas context failed'));
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}
async function generateCV(t: (key: string) => string) {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4'
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  // Layout
  const sidebarW = 62;
  const mainX = sidebarW + 12;
  const mainW = pageW - mainX - 16;
  const sidebarPad = 10;
  // Colors
  const sidebarBg = [26, 26, 26] as const;
  const copper = [200, 149, 108] as const;
  const white = [255, 255, 255] as const;
  const lightGray = [180, 180, 180] as const;
  const dark = [26, 26, 26] as const;
  const muted = [107, 107, 107] as const;
  const cardBg = [248, 248, 247] as const;
  // ===== SIDEBAR =====
  doc.setFillColor(...sidebarBg);
  doc.rect(0, 0, sidebarW, pageH, 'F');
  // Copper accent line at top of sidebar
  doc.setFillColor(...copper);
  doc.rect(0, 0, sidebarW, 3, 'F');
  let sY = 20;
  // --- Photo ---
  try {
    const imgData = await loadImageAsBase64(PHOTO_URL);
    const photoSize = 36;
    const photoX = (sidebarW - photoSize) / 2;
    doc.addImage(imgData, 'JPEG', photoX, sY, photoSize, photoSize);
    // Refined circle border (thinner, more elegant)
    doc.setDrawColor(...copper);
    doc.setLineWidth(0.4);
    doc.roundedRect(photoX, sY, photoSize, photoSize, photoSize / 2, photoSize / 2, 'S');
    sY += photoSize + 10;
  } catch (e) {
    sY += 10;
  }
  // --- Name in sidebar ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...white);
  doc.text('Mick van', sidebarW / 2, sY, {
    align: 'center'
  });
  sY += 5;
  doc.text('Latum', sidebarW / 2, sY, {
    align: 'center'
  });
  sY += 7;
  // Role
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...copper);
  doc.text(t('home.role').toUpperCase(), sidebarW / 2, sY, {
    align: 'center'
  });
  sY += 14;
  // --- Contact Section ---
  doc.setFillColor(40, 40, 40);
  doc.roundedRect(sidebarPad - 2, sY - 3, sidebarW - (sidebarPad - 2) * 2, 42, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...copper);
  doc.text('CONTACT', sidebarPad + 2, sY + 2);
  sY += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...lightGray);
  var contactItems = ['mick@mvlcreative.com', '0651244475', 'Almere, NL', 'linkedin.com/in/mick-van-latum'];
  contactItems.forEach(function (item) {
    doc.text(item, sidebarPad + 2, sY);
    sY += 6;
  });
  sY += 10;
  // --- Skills Section ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...copper);
  doc.text('VAARDIGHEDEN', sidebarPad, sY);
  sY += 8;
  var skills = ['Product Design', 'UX Research', 'Frontend Dev', 'React / TypeScript', 'Websites & Apps', 'Design Systems', 'AI Tooling', 'Figma', 'Wireframing', 'Prototyping'];
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  skills.forEach(function (skill) {
    var tagW = doc.getTextWidth(skill) + 6;
    // Copper pill background
    doc.setFillColor(50, 45, 40);
    doc.roundedRect(sidebarPad, sY - 2.5, tagW, 5.5, 1.5, 1.5, 'F');
    doc.setTextColor(220, 200, 180);
    doc.text(skill, sidebarPad + 3, sY + 1);
    sY += 7.5;
  });
  sY += 6;
  // --- Tools Section ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...copper);
  doc.text('TOOLS', sidebarPad, sY);
  sY += 8;
  var tools = ['Figma', 'VS Code', 'Adobe CC', 'Framer', 'Webflow'];
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...lightGray);
  tools.forEach(function (tool) {
    // Small bullet marker
    doc.setFillColor(...copper);
    doc.circle(sidebarPad + 1.5, sY - 0.5, 0.8, 'F');
    doc.text(tool, sidebarPad + 5, sY);
    sY += 6;
  });
  sY += 6;
  // --- Languages Section ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...copper);
  doc.text('TALEN', sidebarPad, sY);
  sY += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...lightGray);
  doc.text('Nederlands', sidebarPad + 5, sY);
  sY += 4;
  doc.setFontSize(6);
  doc.setTextColor(150, 150, 150);
  doc.text('Moedertaal', sidebarPad + 5, sY);
  sY += 8;
  doc.setFontSize(7);
  doc.setTextColor(...lightGray);
  doc.text('English', sidebarPad + 5, sY);
  sY += 4;
  doc.setFontSize(6);
  doc.setTextColor(150, 150, 150);
  doc.text('Fluent', sidebarPad + 5, sY);
  sY += 10;
  // Decorative dot grid in sidebar footer
  doc.setFillColor(60, 60, 60);
  for (var gx = sidebarPad; gx < sidebarW - sidebarPad; gx += 4) {
    for (var gy = pageH - 30; gy < pageH - 20; gy += 4) {
      doc.circle(gx, gy, 0.3, 'F');
    }
  }
  // ===== MAIN CONTENT =====
  let mY = 20;
  // --- Copper accent bar at top of main area ---
  doc.setFillColor(...copper);
  doc.rect(sidebarW, 0, pageW - sidebarW, 3, 'F');
  // --- Pitch / Summary ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...copper);
  doc.text('PROFIEL', mainX, mY);
  mY += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...muted);
  var pitchLines = doc.splitTextToSize(t('home.pitch'), mainW);
  doc.text(pitchLines, mainX, mY);
  mY += pitchLines.length * 5 + 10;
  // --- Decorative copper accent (small diamond) ---
  doc.setFillColor(...copper);
  var diamondX = mainX + mainW / 2;
  var diamondY = mY + 2;
  doc.triangle(diamondX, diamondY - 2, diamondX - 2, diamondY, diamondX + 2, diamondY, 'F');
  doc.triangle(diamondX, diamondY + 2, diamondX - 2, diamondY, diamondX + 2, diamondY, 'F');
  mY += 8;
  // --- Experience ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...copper);
  doc.text(t('resume.experience').toUpperCase(), mainX, mY);
  mY += 10;
  // Timeline line
  var timelineX = mainX + 3;
  var contentX = mainX + 12;
  var jobs = [{
    role: t('resume.freelance_role'),
    company: t('resume.freelance_company'),
    period: t('resume.freelance_period'),
    description: t('resume.freelance_description'),
    current: true
  }, {
    role: t('resume.webheld_role'),
    company: t('resume.webheld_company'),
    period: t('resume.webheld_period'),
    description: t('resume.webheld_description'),
    current: false
  }, {
    role: t('resume.aim_role'),
    company: t('resume.aim_company'),
    period: t('resume.aim_period'),
    description: t('resume.aim_description'),
    current: false
  }, {
    role: t('resume.tweak_role'),
    company: t('resume.tweak_company'),
    period: t('resume.tweak_period'),
    description: t('resume.tweak_description'),
    current: false
  }];
  var jobContentW = mainW - 14;
  jobs.forEach(function (job, i) {
    // Page break safety check
    if (mY > pageH - 50) {
      return;
    }
    var startY = mY;
    // Calculate card height
    doc.setFontSize(8.5);
    var descLines = doc.splitTextToSize(job.description, jobContentW - 4);
    var cardH = 18 + descLines.length * 4.2;
    // Card background
    doc.setFillColor(...cardBg);
    doc.roundedRect(contentX - 2, mY - 2, jobContentW + 4, cardH, 2, 2, 'F');
    // Timeline dot
    if (job.current) {
      doc.setFillColor(...copper);
      doc.circle(timelineX, mY + 1, 2, 'F');
    } else {
      doc.setDrawColor(...copper);
      doc.setLineWidth(0.5);
      doc.setFillColor(...white);
      doc.circle(timelineX, mY + 1, 1.5, 'FD');
    }
    // Period (right-aligned)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...copper);
    var periodW = doc.getTextWidth(job.period);
    doc.text(job.period, mainX + mainW - periodW, mY + 1.5);
    // Role (slightly larger, better spacing)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...dark);
    doc.text(job.role, contentX, mY + 1.5);
    mY += 6.5;
    // Company
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text(job.company, contentX, mY);
    mY += 5.5;
    // Description
    doc.setFontSize(8.5);
    doc.setTextColor(...muted);
    doc.text(descLines, contentX, mY);
    mY += descLines.length * 4.2 + 10;
    // Timeline line segment (connect to next dot)
    if (i < jobs.length - 1) {
      doc.setDrawColor(220, 210, 200);
      doc.setLineWidth(0.3);
      doc.line(timelineX, startY + 4, timelineX, mY - 6);
    }
  });
  // --- Footer ---
  // Copper line above footer
  doc.setDrawColor(...copper);
  doc.setLineWidth(0.3);
  doc.line(mainX, pageH - 14, mainX + mainW, pageH - 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...copper);
  doc.text('mvlcreative.com', mainX, pageH - 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(150, 150, 150);
  doc.text('Generated from portfolio', mainX, pageH - 5);
  // Small MvlCreative mark in sidebar footer
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.setTextColor(60, 60, 60);
  doc.text('MvlCreative', sidebarW / 2, pageH - 10, {
    align: 'center'
  });
  doc.save('CV_Mick_van_Latum.pdf');
}
export function ResumeApp() {
  const {
    theme,
    t
  } = useTheme();
  const isLightTheme = theme === 'light';
  const [isGenerating, setIsGenerating] = useState(false);
  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      await generateCV(t);
    } finally {
      setIsGenerating(false);
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 28
      }
    }
  };
  const experiences = [{
    role: t('resume.freelance_role'),
    company: t('resume.freelance_company'),
    period: t('resume.freelance_period'),
    description: t('resume.freelance_description'),
    highlight: true
  }, {
    role: t('resume.webheld_role'),
    company: t('resume.webheld_company'),
    period: t('resume.webheld_period'),
    description: t('resume.webheld_description')
  }, {
    role: t('resume.aim_role'),
    company: t('resume.aim_company'),
    period: t('resume.aim_period'),
    description: t('resume.aim_description')
  }, {
    role: t('resume.tweak_role'),
    company: t('resume.tweak_company'),
    period: t('resume.tweak_period'),
    description: t('resume.tweak_description')
  }];
  return <div className="h-full overflow-y-auto p-5 md:p-16 pb-28" data-id="element-69">
      <motion.div className="max-w-3xl mx-auto space-y-12 md:space-y-16" variants={containerVariants} initial="hidden" animate="visible" data-id="element-70">
        {/* Header & Download */}
        <motion.div variants={itemVariants} className={`flex flex-col sm:flex-row sm:items-end justify-between gap-6 pb-8 border-b ${isLightTheme ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-71">
          <div className="space-y-2" data-id="element-72">
            <h1 className={`text-4xl md:text-5xl font-bold tracking-tight ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-73">
              Mick van Latum
            </h1>
            <p className={`font-mono text-sm uppercase tracking-widest ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-74">
              {t('home.role')}
            </p>
          </div>
          <button onClick={handleDownload} disabled={isGenerating} className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#C8956C] text-white font-medium hover:bg-[#B5825A] transition-colors shadow-lg shadow-[#C8956C]/20 disabled:opacity-60" data-id="element-75">
            {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-76" /> : <Download className="w-4 h-4" data-id="element-77" />}
            {isGenerating ? 'Generating...' : t('resume.download')}
          </button>
        </motion.div>

        {/* Experience */}
        <motion.div variants={itemVariants} className="space-y-8" data-id="element-78">
          <div className="flex items-center gap-3" data-id="element-79">
            <Briefcase className={`w-5 h-5 ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-80" />
            <h2 className={`text-2xl font-bold ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-81">
              {t('resume.experience')}
            </h2>
          </div>

          <div className="space-y-4" data-id="element-82">
            {experiences.map((exp, i) => <motion.div key={i} variants={itemVariants} className={`p-6 md:p-8 rounded-xl border backdrop-blur-sm shadow-sm ${exp.highlight ? isLightTheme ? 'bg-white/50 border-[#C8956C]/20 border-l-2 border-l-[#C8956C]' : 'bg-[#1A1A1A]/50 border-[#C8956C]/20 border-l-2 border-l-[#C8956C]' : isLightTheme ? 'bg-white/30 border-black/[0.06]' : 'bg-[#1A1A1A]/30 border-white/[0.06]'}`} data-id="element-83">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3" data-id="element-84">
                  <h3 className={`font-bold text-lg ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-85">
                    {exp.role}
                  </h3>
                  <span className="font-mono text-xs text-[#C8956C]" data-id="element-86">
                    {exp.period}
                  </span>
                </div>
                <div className={`font-medium mb-3 text-sm ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-87">
                  {exp.company}
                </div>
                <p className={`text-base leading-relaxed ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-88">
                  {exp.description}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </motion.div>
    </div>;
}